import { CalendarDays, ExternalLink, X, Newspaper } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate, pickLang } from "@/lib/format";

/**
 * Full-screen news/announcement detail modal.
 *
 * Mirrors EventDetailModal but tuned for news entries:
 *   - shows the flyer (image_url) at full resolution,
 *   - shows the full body (whitespace preserved),
 *   - if registration_url is set, renders a prominent bilingual
 *     "Register / Registrarse" CTA that opens the URL in a new tab.
 *
 * Props:
 *   item     News document (or null/undefined when closed).
 *   onClose  () => void  Called when the user dismisses.
 */
export function NewsDetailModal({ item, onClose }) {
  const { t, lang } = useI18n();
  const open = !!item;
  const title = item ? pickLang(item, "title", lang) : "";
  const body = item ? pickLang(item, "body", lang) : "";
  const image = item?.image_url || "";
  const regUrl = (item?.registration_url || "").trim();
  const hasReg = !!regUrl && /^https?:\/\//i.test(regUrl);

  const openRegistration = () => {
    if (!hasReg) return;
    window.open(regUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-2xl w-[96vw] h-[92vh] sm:h-auto sm:max-h-[92vh] p-0 gap-0 rounded-2xl overflow-hidden flex flex-col"
        data-testid="news-detail-modal"
      >
        {item && (
          <>
            {/* Scrollable body */}
            <div
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
              style={{ WebkitOverflowScrolling: "touch" }}
              data-testid="news-detail-scroll"
            >
              {image && (
                <div className="relative w-full bg-black/5">
                  <img
                    src={image}
                    alt={t.events?.flyerAlt || "Flyer"}
                    className="block w-full h-auto max-h-[50vh] object-contain"
                    data-testid="news-detail-image"
                  />
                </div>
              )}

              <div className="p-5 space-y-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                    <Newspaper className="h-3.5 w-3.5" />
                    <span>{t.home?.latestNews || (lang === "es" ? "Noticias" : "News")}</span>
                  </div>
                  <h2 className="font-serif-display text-2xl font-semibold leading-tight" data-testid="news-detail-title">
                    {title}
                  </h2>
                  <p className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {fmtDate(item.news_date || item.created_at, lang)}
                  </p>
                </div>

                {body && (
                  <p
                    className="whitespace-pre-wrap text-foreground/90 leading-relaxed text-sm"
                    data-testid="news-detail-body"
                  >
                    {body}
                  </p>
                )}

                {hasReg && (
                  <div className="rounded-xl bg-primary/5 ring-1 ring-primary/15 p-3">
                    <p className="text-xs text-muted-foreground">
                      {t.events?.registerHint
                        || (lang === "es"
                          ? "Esta noticia requiere registro previo. Toca abajo para llenar el formulario."
                          : "This announcement requires registration. Tap below to open the form.")}
                    </p>
                    <Button
                      onClick={openRegistration}
                      className="mt-2 w-full sm:w-auto"
                      size="lg"
                      data-testid="news-detail-register"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {t.events?.registerCta || (lang === "es" ? "Registrarse" : "Register")}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Sticky footer so the Close button is always reachable, even
                when the article body is long enough to require scrolling. */}
            <div
              className="shrink-0 border-t border-border/60 bg-background p-3 flex justify-end"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
              data-testid="news-detail-footer"
            >
              <Button variant="outline" onClick={onClose} data-testid="news-detail-close">
                <X className="mr-1 h-4 w-4" />
                {t.events?.close || (lang === "es" ? "Cerrar" : "Close")}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
